/* eslint-disable jsx-a11y/anchor-is-valid */
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Grid, Link, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../../../../../utils/formatters";
import { Notification } from "../../hooks/useNotifications/useNotifications";

const FileDownloadedNotificationV1 = ({
  notification,
  expireNotification,
  successMessage,
  failureMessage,
  downloadFunction,
  subMessage,
}: {
  notification: Notification;
  expireNotification?: () => void;
  successMessage: string;
  failureMessage: string;
  downloadFunction: (accessToken: string) => void;
  subMessage?: string;
}) => {
  const { t, i18n } = useTranslation(["common"]);
  const theme = useTheme();
  const { authState } = useOktaAuth();

  const statusIconStyles = {
    position: "absolute",
    top: "-2%",
    left: "-2%",
    borderRadius: theme.tokens.FuelBorderRadiusFull,
    backgroundColor: theme.tokens.FuelColorBackgroundWhite,
    fontSize: "0.75rem",
  };

  const isFileCompiledSuccessfully =
    notification.payload.compilationStatus === "COMPLETED" ||
    notification.payload.compilationStatus === "PARTIAL_SUCCESS";

  function getNotificationIcon() {
    switch (notification.payload.compilationStatus) {
      case "COMPLETED":
        return (
          <CheckCircleIcon
            data-testid="file-download-successful-icon"
            sx={{
              ...statusIconStyles,
              color: theme.tokens.FuelColorAlertSuccessIcon,
            }}
          />
        );
      case "PARTIAL_SUCCESS":
        return (
          <WarningIcon
            data-testid="file-download-partial-success-icon"
            sx={{
              ...statusIconStyles,
              color: theme.tokens.FuelColorAlertWarningIcon,
            }}
          />
        );
      default:
        return (
          <CancelIcon
            data-testid="file-download-failure-icon"
            sx={{
              ...statusIconStyles,
              color: theme.tokens.FuelColorAlertErrorIcon,
            }}
          />
        );
    }
  }

  return (
    <Box
      data-testid={`notification-item-${notification.payload.id}`}
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: theme.tokens.FuelSpacing3,
      }}
    >
      <Box sx={{ position: "relative" }}>
        {getNotificationIcon()}
        <DescriptionIcon
          sx={{ color: theme.tokens.FuelColorTextIcon, fontSize: "1.7rem" }}
        />
      </Box>
      <Box>
        <Box
          data-testid="notification-content"
          sx={{ paddingBottom: theme.tokens.FuelSpacing1 }}
        >
          <Typography sx={{ fontSize: theme.tokens.FuelFontSizeHeading5 }}>
            {isFileCompiledSuccessfully ? successMessage : failureMessage}
          </Typography>
        </Box>
        <Box>
          {isFileCompiledSuccessfully &&
            notification.payload.downloadUrl &&
            authState && (
              <Typography sx={{ fontSize: theme.tokens.FuelFontSizeXs }}>
                {notification.isExpired ? (
                  <Typography
                    data-testid="download-expired"
                    component="span"
                    sx={{
                      fontSize: theme.tokens.FuelFontSizeXs,
                      color: theme.tokens.FuelColorTextDisabled,
                    }}
                  >
                    {t("notifications.promotionFileDownloadStatus.expired")}
                  </Typography>
                ) : (
                  <Link
                    data-testid="download-link"
                    download
                    onClick={() => {
                      downloadFunction(authState?.accessToken?.accessToken!);
                      if (expireNotification) expireNotification();
                    }}
                  >
                    {t(
                      "notifications.promotionFileDownloadSuccessNotificationMessage.downloadLink"
                    )}
                  </Link>
                )}
                <Typography
                  component="span"
                  sx={{
                    fontSize: theme.tokens.FuelFontSizeXs,
                    color: notification.isExpired
                      ? theme.tokens.FuelColorTextDisabled
                      : theme.tokens.FuelColorTextTertiary,
                  }}
                  data-testid="created-at"
                >
                  {" | "}
                  {formatDate(notification.createdAt.toString(), i18n.language)}
                </Typography>
              </Typography>
            )}
          {subMessage && (
            <Grid
              data-testid="notification-sub-content"
              marginTop={theme.tokens.FuelSpacing1}
            >
              <Typography data-fuel-size="sm" color="text.tertiary">
                {subMessage}
              </Typography>
            </Grid>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FileDownloadedNotificationV1;
